import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, X, Check, ArrowRight, GraduationCap } from "lucide-react";
import CefrBadge from "@/components/CefrBadge";
import client from "@/lib/api";
import { useLang } from "@/context/LangContext";

export default function Placement() {
  const { language } = useParams();
  const nav = useNavigate();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    client.get(`/learn/placement/${language}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || t("pl_unavailable")));
  }, [language]);

  if (error) return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center px-6 text-center">
      <p className="text-[#475569]" data-testid="placement-error">{error}</p>
      <button onClick={() => nav("/learn")} className="mt-5 bg-[#2563EB] text-white font-bold px-6 py-3 rounded-2xl">{t("common_back_to_learn")}</button>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center"><Loader2 className="animate-spin text-[#2563EB]" /></div>;

  if (result) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center px-6 pt-20 text-center" data-testid="placement-result">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#2563EB] to-[#3B82F6] flex items-center justify-center shadow-[0_18px_44px_-16px_rgba(37,99,235,0.85)]"><GraduationCap className="text-white" size={38} /></div>
        <h1 className="font-heading text-2xl font-extrabold text-[#0F172A] mt-5">{t("pl_complete")}</h1>
        <p className="text-[#475569] mt-1">{t("pl_answered", { c: result.correct, t: result.total })}</p>
        <div className="mt-6 rounded-2xl bg-white border border-slate-200/70 p-6 w-full max-w-[340px]">
          <p className="text-sm text-[#475569]">{t("pl_recommended")}</p>
          <div className="mt-2 flex justify-center"><CefrBadge level={result.recommended_level} size="lg" /></div>
          <p className="text-xs text-[#475569] mt-3">{t("pl_note")}</p>
        </div>
        <button onClick={() => nav("/learn")} data-testid="placement-start-learning" className="w-full max-w-[340px] mt-7 bg-[#2563EB] text-white font-bold py-4 rounded-2xl">{t("pl_start")}</button>
      </div>
    );
  }

  const q = data.questions[idx];
  const sel = answers[idx];
  const isLast = idx === data.questions.length - 1;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data: r } = await client.post(`/learn/placement/${language}/submit`, { answers });
      setResult(r);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col px-5">
      <div className="flex items-center justify-between pt-6">
        <button onClick={() => nav("/learn")} data-testid="placement-close" className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center"><X className="text-slate-600" size={20} /></button>
        <span className="text-sm font-bold text-[#0F172A]">{t("pl_test")}</span>
        <span className="text-sm font-extrabold text-[#2563EB]">{idx + 1}/{data.questions.length}</span>
      </div>
      <div className="mt-3 h-2 rounded-full bg-[#EDF2F7] overflow-hidden">
        <div className="h-full rounded-full bg-[#2563EB] transition-all" style={{ width: `${((idx + 1) / data.questions.length) * 100}%` }} />
      </div>

      <div className="flex-1 flex flex-col justify-center">
        <div className="rounded-[24px] bg-white border border-slate-200/70 shadow-[0_20px_44px_-28px_rgba(15,23,42,0.4)] p-5">
          <h2 className="font-heading text-xl font-extrabold text-[#0F172A]">{q.prompt}</h2>
          <div className="mt-4 space-y-3">
            {q.options.map((o, i) => (
              <button key={i} onClick={() => setAnswers({ ...answers, [idx]: i })} data-testid={`placement-opt-${i}`}
                className={`w-full text-left rounded-2xl px-4 py-3.5 border font-semibold ${sel === i ? "border-[#2563EB] bg-[#2563EB]/5 text-[#0F172A]" : "border-slate-200 bg-white text-[#0F172A]"}`}>
                {o}{sel === i && <Check size={16} className="inline ml-2 text-[#2563EB]" />}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="pb-8">
        {!isLast ? (
          <button onClick={() => setIdx(idx + 1)} disabled={sel === undefined} data-testid="placement-next"
            className="w-full bg-[#2563EB] text-white font-bold py-4 rounded-2xl disabled:opacity-50 flex items-center justify-center gap-2">{t("common_next")} <ArrowRight size={18} /></button>
        ) : (
          <button onClick={submit} disabled={sel === undefined || submitting} data-testid="placement-submit"
            className="w-full bg-[#2563EB] text-white font-bold py-4 rounded-2xl disabled:opacity-50 flex items-center justify-center gap-2">
            {submitting ? <Loader2 className="animate-spin" size={18} /> : t("pl_see_level")}
          </button>
        )}
      </div>
    </div>
  );
}
